import React, { ChangeEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Label, Spinner } from "reactstrap";

import { PDF_ACCEPT } from "../../constants";
import { MediaTypeEnum } from "../../enums";
import { MediaDto } from "../../interfaces/media";
import { formatFileSize } from "../../services";
import {
  useDeleteLiveLectureMediaMutation,
  useUploadLiveLectureNotesMutation,
} from "../../store/api/liveLectureApi";

type TProps = {
  liveLectureId?: string | null;
  media?: MediaDto[];
  pendingNotes: MediaDto[];
  onPendingNotesChange: (media: MediaDto[]) => void;
  disabled?: boolean;
};

export const LiveLectureNotes: React.FC<TProps> = ({
  liveLectureId,
  media = [],
  pendingNotes,
  onPendingNotesChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const [uploadNotes, { isLoading: isUploading }] =
    useUploadLiveLectureNotesMutation();
  const [deleteMedia, { isLoading: isDeleting }] =
    useDeleteLiveLectureMediaMutation();

  const noteItems = useMemo(() => {
    const items = liveLectureId ? media : pendingNotes;
    return items.filter((item) => item.type === MediaTypeEnum.DOCUMENT);
  }, [liveLectureId, media, pendingNotes]);

  const isBusy = isUploading || isDeleting;

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      setUploadProgress(0);
      const uploaded = await uploadNotes({
        file,
        liveLectureId,
        onProgress: setUploadProgress,
      }).unwrap();

      if (!liveLectureId) {
        onPendingNotesChange([...pendingNotes, uploaded]);
      }
    } finally {
      setUploadProgress(null);
    }
  };

  const onRemove = async (item: MediaDto) => {
    if (liveLectureId) {
      await deleteMedia({
        liveLectureId,
        mediaId: item.id,
      });
      return;
    }

    onPendingNotesChange(pendingNotes.filter((note) => note.id !== item.id));
  };

  return (
    <div className="mb-3">
      <Label className="form-label">{t("liveLectureNotes")}</Label>
      <p className="text-muted small mb-3">{t("liveLectureNotesHint")}</p>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Label
          className={`btn btn-soft-danger mb-0 ${disabled || isBusy ? "disabled" : ""}`}
          htmlFor="live-lecture-notes-upload"
        >
          <i className="ri-file-upload-line me-1"></i>
          {t("uploadPdf")}
        </Label>
        <input
          disabled={disabled || isBusy}
          type="file"
          className="d-none"
          id="live-lecture-notes-upload"
          accept={PDF_ACCEPT}
          onChange={onUpload}
        />

        {uploadProgress !== null && (
          <span className="d-inline-flex align-items-center text-muted small">
            <Spinner size="sm" className="me-2" />
            {t("uploading")} {uploadProgress}%
          </span>
        )}
      </div>

      {noteItems.length === 0 ? (
        <div className="text-muted small">{t("liveLectureNotesEmpty")}</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {noteItems.map((item) => (
            <div
              key={item.id}
              className="border rounded p-3 d-flex flex-wrap align-items-center justify-content-between gap-2"
            >
              <div className="d-flex align-items-center gap-3 min-w-0">
                <i className="ri-file-pdf-line text-danger fs-4"></i>
                <div className="min-w-0">
                  <div className="text-truncate fw-medium" title={item.name}>
                    {item.name}
                  </div>
                  <div className="text-muted small">
                    {formatFileSize(Number(item.size) || 0)}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {item.path ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-soft-primary"
                  >
                    <i className="ri-download-2-line me-1"></i>
                    {t("download")}
                  </a>
                ) : null}
                <Button
                  color="link"
                  className="text-danger p-0"
                  onClick={() => onRemove(item)}
                  disabled={disabled || isBusy}
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
